import React, { useState, useEffect } from 'react';
import { Form, Input, Space, Button, Table, Select, InputNumber, Upload, Modal, Popconfirm, message } from 'antd';
import { DeleteOutlined, EditOutlined, SearchOutlined, PlusOutlined } from '@ant-design/icons';
import EditGenerationForm from './EditGenerationForm';
import Highlighter from 'react-highlight-words';
import InputRules from '../../data/InputRules';
import { connect } from 'react-redux';
import { Actions } from '../../store/actions';
import ReactImage from '../../components/Image';
import { toFormData } from '../../util/FormData';

const layout = {
	labelCol: {
		span: 3,
	},
	wrapperCol: {
		span: 8,
	},
};
const tailLayout = {
	wrapperCol: {
		offset: 3,
		span: 16,
	},
};

const uploadButton = (
	<div>
		<PlusOutlined />
		<div style={{ marginTop: 8 }}>Upload</div>
	</div>
);

const TrimManagement = ({
	models,
	makes,
	generations,
	utils,
	fetchMakes,
	fetchModels,
	fetchGenerations,
	addGeneration,
	deleteGeneration,
	updateGeneration,
	fetchVehicleUtils,
}) => {
	const [selectedMake, setSelectedMake] = useState();
	const [filteredModels, setFilteredModels] = useState();
	const [refresh, setRefresh] = useState(false);
	const [editModal, setEditModal] = useState(false);
	const [editGeneration, setEditGeneration] = useState();
	const [searchText, setSearchText] = useState('');
	const [searchedColumn, setSearchedColumn] = useState('');
	const [filelist, setFileList] = useState([]);
	const [imageList, setImageList] = useState([]);

	useEffect(() => {
		fetchMakes();
		fetchModels();
		fetchGenerations();
		fetchVehicleUtils();
	}, [refresh]);

	useEffect(() => {
		setFilteredModels(models.filter((item) => item.make === selectedMake));
	}, [selectedMake]);

	useEffect(() => {
		if (filelist.length) {
			const file = filelist[filelist.length - 1].originFileObj;
			file.thumbUrl = URL.createObjectURL(file);
			const list = imageList;
			list.push(file);
			setImageList(list);
		}
	}, [filelist]);

	const getColumnSearchProps = (dataIndex) => ({
		filterDropdown: function filterDropdown({ setSelectedKeys, selectedKeys, confirm, clearFilters }) {
			return (
				<div style={{ padding: 8 }}>
					<Input
						placeholder={`Search ${dataIndex}`}
						value={selectedKeys[0]}
						onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
						onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
						style={{ width: 188, marginBottom: 8, display: 'block' }}
					/>
					<Space>
						<Button
							type="primary"
							onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
							icon={<SearchOutlined />}
							size="small"
							style={{ width: 90 }}
						>
							Search
						</Button>
						<Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
							Reset
						</Button>
					</Space>
				</div>
			);
		},
		filterIcon: function filter(filtered) {
			return <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />;
		},
		onFilter: (value, record) =>
			record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : '',
		render: function render(text) {
			return searchedColumn === dataIndex ? (
				<Highlighter
					highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
					searchWords={[searchText]}
					autoEscape
					textToHighlight={text ? text.toString() : ''}
				/>
			) : (
				text
			);
		},
	});

	const handleSearch = (selectedKeys, confirm, dataIndex) => {
		confirm();
		setSearchText(selectedKeys[0]);
		setSearchedColumn(dataIndex);
	};

	const handleReset = (clearFilters) => {
		clearFilters();
		setSearchText('');
	};

	const columns = [
		{
			title: 'Image',
			dataIndex: 'images',
			key: 'images',
			render: function image(text, record) {
				return (
					<Space size="middle">{record?.images?.length > 0 && <ReactImage src={record?.images[0]} width={50} />}</Space>
				);
			},
		},
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			sorter: (a, b) => {
				if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
				if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
				return 0;
			},
			...getColumnSearchProps('name'),
		},
		{
			title: 'Make',
			dataIndex: 'make',
			key: 'make',
			sorter: (a, b) => {
				if (a?.make?.toLowerCase() < b?.make?.toLowerCase()) return -1;
				if (a?.make?.toLowerCase() > b?.make?.toLowerCase()) return 1;
				return 0;
			},
			...getColumnSearchProps('make'),
		},
		{
			title: 'Model',
			dataIndex: 'model',
			key: 'model',
			sorter: (a, b) => {
				if (a?.model?.toLowerCase() < b?.model?.toLowerCase()) return -1;
				if (a?.model?.toLowerCase() > b?.model?.toLowerCase()) return 1;
				return 0;
			},
			...getColumnSearchProps('model'),
		},
		{
			title: 'Body Type',
			dataIndex: 'bodyType',
			key: 'bodyType',
			sorter: (a, b) => {
				if (a.bodyType && b.bodyType) {
					if (a.bodyType.toLowerCase() < b.bodyType.toLowerCase()) return -1;
					if (a.bodyType.toLowerCase() > b.bodyType.toLowerCase()) return 1;
					return 0;
				}
			},
			...getColumnSearchProps('bodyType'),
		},
		{
			title: 'Category',
			dataIndex: 'category',
			key: 'category',
		},
		{
			title: 'Year',
			dataIndex: 'year',
			key: 'year',
			sorter: (a, b) => a.year - b.year,
			...getColumnSearchProps('year'),
		},
		{
			title: 'Action',
			key: 'action',
			render: function action(text, record) {
				return (
					<Space size="middle">
						<EditOutlined
							onClick={() => {
								setEditGeneration(record);
								setEditModal(!editModal);
							}}
						/>
						<Popconfirm
							title="Are you sure to delete this trim?"
							onConfirm={() => onDelete(record._id)}
							okText="Yes"
							cancelText="No"
						>
							<DeleteOutlined />
						</Popconfirm>
					</Space>
				);
			},
		},
	];

	const onFinish = async (values) => {
		values.images = imageList;
		const data = toFormData(values);
		await addGeneration(data);
		setRefresh(!refresh);
	};

	const onFinishFailed = (errorInfo) => {
		message.error('Something went wrong!');
	};

	const onDelete = async (id) => {
		await deleteGeneration(id);
		setRefresh(!refresh);
	};

	const handleChange = ({ fileList }) => {
		setFileList(fileList);
	};

	return (
		<div>
			<div>
				<Form
					{...layout}
					name="trim-form"
					initialValues={{ remember: true }}
					onFinish={onFinish}
					onFinishFailed={onFinishFailed}
				>
					<Form.Item label="Make" name="make" rules={InputRules.required('Please select make!')}>
						<Select
							showSearch
							allowClear={true}
							placeholder="Select a make"
							onChange={(value) => setSelectedMake(value)}
						>
							{makes.map((make) => (
								<Select.Option value={make._id} key={make._id}>
									{make._id}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
					<Form.Item label="Model" name="model" rules={InputRules.required('Please select model!')}>
						<Select showSearch allowClear={true} placeholder="Select a model">
							{filteredModels?.map((model) => (
								<Select.Option value={model.name} key={model._id}>
									{model.name}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
					<Form.Item label="Trim" name="name" rules={InputRules.required('Please input trim/variant!')}>
						<Input placeholder="Enter trim name" />
					</Form.Item>
					<Form.Item label="Generation" name="generation" rules={InputRules.required('Please input trim generation')}>
						<Input placeholder="Enter gerneration name" />
					</Form.Item>
					<Form.Item label="Upload Image" name="images" rules={InputRules.required('Please enter image!')}>
						<Upload
							accept=".jpg,.png,.jpeg"
							listType="picture-card"
							onChange={handleChange}
							fileList={filelist}
							beforeUpload={() => false}
						>
							{uploadButton}
						</Upload>
					</Form.Item>
					<Form.Item label="Nick Name" name="nickName" rules={InputRules.required('Please input trim nick name')}>
						<Input placeholder="Enter nick name" />
					</Form.Item>
					<Form.Item label="Body Type" name="bodyType" rules={InputRules.required('Please select body type!')}>
						<Select showSearch allowClear={true} placeholder="Select a body type">
							{utils?.bodyTypes?.map((bodyType, index) => (
								<Select.Option value={bodyType._id} key={index}>
									{bodyType._id}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
					<Form.Item
						label="Transmission"
						name="transmission"
						rules={InputRules.required('Please select transmission!')}
					>
						<Select showSearch allowClear={true} mode="multiple" placeholder="Select a transmission">
							<Select.Option value="automatic">Automatic</Select.Option>
							<Select.Option value="manual">Manual</Select.Option>
						</Select>
					</Form.Item>
					<Form.Item label="Features" name="features" rules={InputRules.required('Please select features!')}>
						<Select showSearch allowClear={true} placeholder="Select a features" mode="tags">
							{utils?.features?.map(
								(feature) =>
									feature._id === 'Car Features' &&
									feature.features.map((feat) => (
										<Select.Option value={feat} key={feat}>
											{feat}
										</Select.Option>
									))
							)}
						</Select>
					</Form.Item>
					<Form.Item label="Category" name="category" rules={InputRules.required('Please select category!')}>
						<Select showSearch allowClear={true} placeholder="Select a category">
							<Select.Option value="car">Car</Select.Option>
							<Select.Option value="bike">Bike</Select.Option>
						</Select>
					</Form.Item>
					<Form.Item label="Starting Year" name="yearStart" rules={InputRules.required('Please input year!')}>
						<InputNumber />
					</Form.Item>
					<Form.Item label="Ending Year" name="yearEnd">
						<InputNumber />
					</Form.Item>
					<Form.Item
						label="Engine Type"
						name={['engine', 'type']}
						rules={InputRules.required('Please select engine type!')}
					>
						<Select showSearch allowClear={true} placeholder="Select a engine type">
							{utils?.engineTypes?.map((engineType, index) => (
								<Select.Option value={engineType._id.toLowerCase()} key={index}>
									{engineType._id}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
					<Form.Item
						label="Engine Capacity"
						name={['engine', 'size']}
						rules={InputRules.required('Please input engine capacity!')}
					>
						<InputNumber />
					</Form.Item>
					<Form.Item
						label="Engine Horsepower"
						name={['engine', 'horsepower']}
						rules={InputRules.required('Please input engine horsepower!')}
					>
						<InputNumber />
					</Form.Item>
					<Form.Item
						label="Engine Average"
						name={['engine', 'average']}
						rules={InputRules.required('Please input engine average!')}
					>
						<InputNumber />
					</Form.Item>
					<Form.Item {...tailLayout}>
						<Button type="primary" htmlType="submit">
							Add Trim
						</Button>
					</Form.Item>
				</Form>
			</div>
			<div>
				<Table
					dataSource={generations}
					columns={columns}
					rowKey={(record) => record._id}
					size="small"
					scroll={{ x: true }}
				/>
			</div>
			<Modal
				title={`Edit ${editGeneration?.name}`}
				centered={true}
				visible={editModal}
				footer={null}
				onCancel={() => setEditModal(false)}
				destroyOnClose={true}
			>
				<EditGenerationForm
					generation={editGeneration}
					makes={makes}
					models={models}
					features={utils?.features}
					utils={utils}
					updateGeneration={updateGeneration}
					setEditModal={() => setEditModal(false)}
					setRefresh={() => setRefresh(!refresh)}
				/>
			</Modal>
		</div>
	);
};

const mapStateToProps = (store) => ({
	models: store.vehicleModel.models,
	makes: store.vehicleMake.makes,
	generations: store.vehicleGeneration.generations,
	utils: store.vehicleUtil.utils,
});

const mapDispatchToProps = (dispatch) => ({
	fetchMakes: () => dispatch(Actions.fetchMakes()),
	fetchModels: () => dispatch(Actions.fetchModels()),
	fetchGenerations: () => dispatch(Actions.fetchGenerations()),
	addGeneration: (body) => dispatch(Actions.addGeneration(body)),
	deleteGeneration: (id) => dispatch(Actions.deleteGeneration(id)),
	updateGeneration: (id, body) => dispatch(Actions.updateGeneration(id, body)),
	fetchVehicleUtils: () => dispatch(Actions.fetchVehicleUtils()),
});

export default connect(mapStateToProps, mapDispatchToProps)(TrimManagement);
